import { useEffect, useMemo, useRef, useState } from 'react';

// Daily activity as three small multiples: visits run an order of magnitude above
// searches and downloads, so each measure gets its own scale rather than sharing
// one axis. One hue throughout (#2a78d6 light / #3987e5 dark — validated against
// the white and dark card surfaces); each chart's title names what it plots.

const MAX_DAYS = 45;
const PLOT_HEIGHT = 96;
const AXIS_BAND = 20;
const TOP_PAD = 14;
const MAX_BAR = 24;

const SERIES = [
  { key: 'visits', label: 'Visits' },
  { key: 'searches', label: 'Face searches' },
  { key: 'downloads', label: 'Downloads' },
];

const dayLabel = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

/** Every day from the first to the last active day (last MAX_DAYS), zero-filled. */
const fillDays = (daily) => {
  if (!daily.length) return [];
  const byDate = Object.fromEntries(daily.map((d) => [d.date, d]));
  const last = new Date(`${daily[daily.length - 1].date}T00:00:00`);
  let cursor = new Date(`${daily[0].date}T00:00:00`);
  const earliest = new Date(last);
  earliest.setDate(earliest.getDate() - (MAX_DAYS - 1));
  if (cursor < earliest) cursor = earliest;

  const days = [];
  while (cursor <= last) {
    const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
    days.push({ date: iso, visits: 0, searches: 0, downloads: 0, ...byDate[iso] });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const barPath = (x, y, w, h) => {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

const ColumnChart = ({ days, seriesKey, label, emptyNote }) => {
  const [ref, width] = useWidth();
  const [active, setActive] = useState(null);
  const values = days.map((d) => d[seriesKey]);
  const total = values.reduce((sum, v) => sum + v, 0);
  const max = Math.max(...values, 0);

  const band = days.length ? width / days.length : 0;
  const barWidth = Math.max(1, Math.min(MAX_BAR, band - 2, band * 0.7));
  const scale = (v) => (max ? (v / max) * PLOT_HEIGHT : 0);
  const baseline = TOP_PAD + PLOT_HEIGHT;

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <span className="text-sm font-semibold text-slate-900 dark:text-white">{total.toLocaleString()}</span>
      </div>
      <div ref={ref} className="relative" style={{ height: TOP_PAD + PLOT_HEIGHT + AXIS_BAND }}>
        {emptyNote && total === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 text-center px-4">
            {emptyNote}
          </div>
        ) : width > 0 && (
          <svg width={width} height={TOP_PAD + PLOT_HEIGHT + AXIS_BAND} role="img" aria-label={`${label} per day`}>
            <line x1={0} x2={width} y1={TOP_PAD} y2={TOP_PAD} strokeWidth={1} className="stroke-slate-100 dark:stroke-slate-800" />
            <line x1={0} x2={width} y1={baseline + 0.5} y2={baseline + 0.5} strokeWidth={1} className="stroke-slate-200 dark:stroke-slate-700" />
            <text x={0} y={TOP_PAD - 4} fontSize={10} className="fill-slate-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {max.toLocaleString()}
            </text>
            {days.map((d, i) => {
              const h = scale(d[seriesKey]);
              const x = i * band + (band - barWidth) / 2;
              return (
                <g key={d.date}>
                  {h > 0 && (
                    <path
                      d={barPath(x, baseline - h, barWidth, h)}
                      className={`fill-[#2a78d6] dark:fill-[#3987e5] transition-opacity ${active !== null && active !== i ? 'opacity-40' : ''}`}
                    />
                  )}
                  <rect
                    x={i * band}
                    y={0}
                    width={Math.max(band, 1)}
                    height={baseline}
                    fill="transparent"
                    tabIndex={0}
                    aria-label={`${dayLabel(d.date)}: ${d[seriesKey]} ${label.toLowerCase()}`}
                    onPointerEnter={() => setActive(i)}
                    onPointerLeave={() => setActive(null)}
                    onFocus={() => setActive(i)}
                    onBlur={() => setActive(null)}
                    style={{ outline: 'none' }}
                  />
                </g>
              );
            })}
            <text x={0} y={baseline + 14} fontSize={10} className="fill-slate-400">{dayLabel(days[0].date)}</text>
            {days.length > 1 && (
              <text x={width} y={baseline + 14} fontSize={10} textAnchor="end" className="fill-slate-400">
                {dayLabel(days[days.length - 1].date)}
              </text>
            )}
          </svg>
        )}
        {active !== null && width > 0 && (
          <div
            className="pointer-events-none absolute z-10 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 shadow-sm whitespace-nowrap"
            style={{
              left: Math.min(Math.max(active * band + band / 2, 40), width - 40),
              top: 0,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="text-sm font-semibold text-slate-900 dark:text-white">{days[active][seriesKey].toLocaleString()}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">{dayLabel(days[active].date)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const ActivityColumns = ({ daily = [], trackingSince }) => {
  const days = useMemo(() => fillDays(daily), [daily]);
  const [showTable, setShowTable] = useState(false);
  if (!days.length) return null;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SERIES.map((s) => (
          <ColumnChart
            key={s.key}
            days={days}
            seriesKey={s.key}
            label={s.label}
            emptyNote={s.key === 'visits' && !trackingSince ? 'Visit tracking starts with the next link open' : null}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setShowTable((v) => !v)}
        className="mt-3 text-xs font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
      >
        {showTable ? 'Hide table' : 'Show as table'}
      </button>
      {showTable && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                <th className="py-1 pr-4 font-medium">Date</th>
                {SERIES.map((s) => <th key={s.key} className="py-1 pr-4 font-medium text-right">{s.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {days.filter((d) => d.visits || d.searches || d.downloads).reverse().map((d) => (
                <tr key={d.date} className="text-slate-700 dark:text-slate-300">
                  <td className="py-1 pr-4">{dayLabel(d.date)}</td>
                  {SERIES.map((s) => <td key={s.key} className="py-1 pr-4 text-right">{d[s.key]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityColumns;
